import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { TrashAPI } from '@makyoapp/makyo-sdk'
import { DatabasesAPI } from '@makyoapp/makyo-sdk/api/Databases'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import {
  DbItemAdapter,
  DbItemField,
  DbSource,
  fromSdkTrashList,
  PaginatedItems,
  TrashItemDetail,
  TrashItemTy,
} from '@makyoapp/makyo-common'
import { Shield16, Trash16 } from '@makyoapp/makyo-icon/icon'
import {
  avatarClassName,
  AvatarSize,
  DeleteModal,
  getDbSourceIcon,
  Loading,
  ModalGrid,
  MoreDropdown,
  ShapeVariant,
  Table,
  Td,
  Th,
  Tr,
  WrappedPagination,
} from '@makyoapp/makyo-ui'

import { lazyModal } from '@/lib/lazy-load'
import { toasted } from '@/lib/toast'
import { CreateDbProvider } from '@/context/CreateDbContext'
import { useDbContext } from '@/context/DbContext'
import { useDateFmt } from '@/hooks/useDateFmt'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useReqError } from '@/hooks/useReqError'
import {
  DB_FIELDS,
  DEFAULT_ROW_PER_PAGE,
  ORG_DATABASE_ROUTE,
  ORG_ROUTE,
  PROJECT_ROUTE,
  STORAGE_KEYS,
} from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import { getFileSize } from '@/shared/utils'
import { Info } from '@/components/Info'
import Layout from '@/components/Layout'
import { StackedAppTd, StackedTeamTd } from '@/components/TdItem'
import { TrNotFound } from '@/components/TrNotFound'

import { CreateDbModal, CreateDbSelectTeam } from './OrgDetailCreateDb'

const BasicInfoEditModal = lazyModal(() => import('@/components/BasicInfoEditModal'))

/**
 * render database table cell
 *
 * current field to show
 * @param field
 *
 * current db item object
 * @param item
 */
function DbItem({ field, item }: { field: DbItemField; item: DbItemAdapter }) {
  const dateFmtDefault = useDateFmt()
  switch (field) {
    case 'created':
      return <Td className='whitespace-nowrap'>{dateFmtDefault(item.created)}</Td>
    case 'updated':
      return <Td className='whitespace-nowrap'>{dateFmtDefault(item.updated)}</Td>
    case 'source': {
      const { icon, color, title } = getDbSourceIcon(item.source as DbSource)
      const node = (
        <div
          className={clsx(
            'border-4 border-white dark:border-slate-800',
            avatarClassName(true, ShapeVariant.Circle, AvatarSize.M2)
          )}
          style={{
            background: color,
          }}
          title={title}
        >
          {icon}
        </div>
      )
      return <Td>{node}</Td>
    }
    case 'name':
      return (
        <Td className='name db-name'>
          <Link className='link' to={`${ORG_ROUTE}/${item.org.id}/${ORG_DATABASE_ROUTE}/${item.id}`}>
            {item.name}
          </Link>
        </Td>
      )
    case 'teams':
      return <StackedTeamTd items={item.teams} className='db-teams' />
    case 'apps':
      return (
        <StackedAppTd
          items={item.apps}
          className='db-apps'
          renderLink={(node, item) => {
            return <Link to={`${PROJECT_ROUTE}/${item.id}`}>{node}</Link>
          }}
        />
      )
    case 'org':
      return <Td className='name db-org'>org</Td>
    case 'size': {
      const sz = getFileSize(item.size)
      return <Td className='name db-size'>{sz}</Td>
    }
    default:
      return <Td>{item[field].toString()}</Td>
  }
}

/**
 * display list of database in table
 */
function DbTable() {
  const { t } = useTranslation()
  const [delDb, setDelDb] = useState<DbItemAdapter | undefined>(undefined)
  const [editDb, setEditDb] = useState<DbItemAdapter | undefined>(undefined)
  const [editPerm, setEditPerm] = useState<DbItemAdapter | undefined>(undefined)
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  const { isLoading, currentItems, allItems, setAllItems, updateDb } = useDbContext()

  const [trashDb, setTrashDb] = useState<PaginatedItems<TrashItemTy<TrashItemDetail>>>({
    items: [],
    totalCount: 0,
  })

  useEffect(() => {
    TrashAPI.ReqList()
      .then((res) => {
        setTrashDb({
          items: fromSdkTrashList(res),
          totalCount: res.length,
        })
      })
      .catch((e) => {
        console.error(`[TrashAPI.ReqList]: ${e.status} - ${e.message}`)
      })
  }, [])

  const closeEditPerm = () => {
    setIsBtnLoading(false)
    setEditPerm(undefined)
  }

  const closeEditName = () => {
    setIsBtnLoading(false)
    setEditDb(undefined)
  }

  const closeDel = () => {
    setIsBtnLoading(false)
    setDelDb(undefined)
  }

  function hasDeletedDataInTrash(trashData: string[], deletedData: string[]) {
    const trashSet = new Set(trashData)

    return deletedData.some((id) => trashSet.has(id))
  }

  const hasMenu = useOrgPerm(OrgPermission.DBAssignTeamsToDB)
  const printAPIError = useReqError()

  return (
    <>
      <Table className='border bdr'>
        <thead>
          <Tr>
            {DB_FIELDS.map((key) => {
              return (
                <Th key={key} className={clsx(key === 'name' && 'name')}>
                  <strong className='font-semibold text-light flex-c'>
                    {key === 'apps' ? t('Apps') : t(key.replace('Count', 's'))}
                  </strong>
                </Th>
              )
            })}
            <th className='act' />
          </Tr>
        </thead>
        <tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={DB_FIELDS.length + 1}>
                <Loading fullHeight={false} />
              </Td>
            </Tr>
          ) : currentItems.items.length < 1 ? (
            <TrNotFound colSpan={DB_FIELDS.length + 1} />
          ) : (
            currentItems.items.map((db, rowIdx) => {
              return (
                <Tr key={`tbl-row-${rowIdx}`}>
                  {DB_FIELDS.map((k) => (
                    <DbItem key={`${k}-${rowIdx}`} field={k} item={db} />
                  ))}
                  <td className='act'>
                    {hasMenu && (
                      <MoreDropdown id={`db-more-${db.id}`} placement='bottom-end'>
                        {/* <MoreDropdown.MenuItem
                          className='db-locale-edit'
                          id={`${db.id}-db-locale-act`}
                          onClick={() => {
                            // TODO:
                            //  update context
                            // nothing yet
                          }}
                        >
                          <EditDomain16 />
                          <span>{t('Setting')}</span>
                        </MoreDropdown.MenuItem> */}
                        <MoreDropdown.MenuItem
                          className='db-edit-act'
                          id={`${db.id}-db-edit-act`}
                          onClick={() => setEditDb(db)}
                        >
                          <span className='material-symbols-rounded text-[1rem]!'>edit_note</span>
                          <span>{t('Rename')}</span>
                        </MoreDropdown.MenuItem>
                        <MoreDropdown.MenuItem
                          className='db-edit-perm-act'
                          id={`${db.id}-db-edit-perm-act`}
                          onClick={() => setEditPerm(db)}
                        >
                          <Shield16 />
                          <span>{t('Assign Teams')}</span>
                        </MoreDropdown.MenuItem>
                        <MoreDropdown.MenuItem
                          className='db-delete-act red'
                          id={`${db.id}-db-delete-act`}
                          onClick={() => setDelDb(db)}
                        >
                          <Trash16 />
                          <span>{t('Remove')}</span>
                        </MoreDropdown.MenuItem>
                      </MoreDropdown>
                    )}
                  </td>
                </Tr>
              )
            })
          )}
        </tbody>
      </Table>
      {editDb && (
        <BasicInfoEditModal
          id='org-db-edit-modal'
          show={true}
          name={editDb.name}
          title={t('Rename Database')}
          onHide={closeEditName}
          isLoading={isBtnLoading}
          withAvatar={false}
          onSubmit={(newName, _, done) => {
            setIsBtnLoading(true)
            DatabasesAPI.ReqRename(editDb.id, newName)
              .then(() => {
                updateDb(editDb.id, (prev) => {
                  return {
                    ...prev,
                    name: newName,
                  }
                })
                toasted(t('Database updated!'))
              })
              .catch((e) => {
                printAPIError(e)
              })
              .finally(() => {
                done?.()
                closeEditName()
              })
          }}
        />
      )}
      {editPerm && (
        <ModalGrid
          id='db-assign-perm-modal'
          show={true}
          onHide={closeEditPerm}
          onCancel={closeEditPerm}
          title={t('Edit Teams')}
          primaryTrailing={t('Update Permission')}
          isLoading={isBtnLoading}
          onConfirm={() => {
            setIsBtnLoading(true)
            const newTeam = editPerm?.teams.map((dbt) => dbt.id)
            DatabasesAPI.ReqUpdateTeams(editPerm?.id, {
              _Teams: newTeam,
            })
              .then(() => {
                if (editPerm) {
                  updateDb(editPerm.id, (prev) => {
                    return {
                      ...prev,
                      teams: editPerm?.teams,
                    }
                  })
                }
                toasted(t('Permission updated!'))
              })
              .catch((e) => {
                printAPIError(e)
              })
              .finally(() => {
                closeEditPerm()
              })
          }}
        >
          <CreateDbSelectTeam
            forcedTeams={[]}
            value={editPerm.teams}
            setValue={(teams) => {
              setEditPerm({
                ...editPerm,
                teams,
              })
            }}
          />
        </ModalGrid>
      )}
      {delDb &&
        (delDb?.apps?.length > 0 ? (
          <DeleteModal
            id='db-warning-modal'
            show={true}
            onHide={closeDel}
            onSubmit={closeDel}
            continueLabel={t('I understand')}
            title={t('Cannot Delete Database')}
            withCancel
          >
            <Info className='items-start!'>
              {(() => {
                const trashAppIds =
                  trashDb?.items?.filter((trash) => trash.type === 'App')?.map((trash) => trash?.item?.id) || []

                const deletedAppIds = delDb.apps.map((app) => app.id)

                return hasDeletedDataInTrash(trashAppIds, deletedAppIds) ? (
                  <Trans>
                    This database cannot be deleted because there is at least one app in the trash that relies on it. To
                    proceed, please permanently delete the app from the trash and try again.
                  </Trans>
                ) : (
                  <Trans>
                    This database cannot be deleted because at least one app is using it. First, remove the app from the
                    Apps page, then permanently delete it from the trash, and try again.
                  </Trans>
                )
              })()}
            </Info>
          </DeleteModal>
        ) : (
          <DeleteModal
            id='db-delete-modal'
            show={true}
            onHide={closeDel}
            isLoading={isBtnLoading}
            withCancel
            onSubmit={() => {
              setIsBtnLoading(true)
              DatabasesAPI.ReqDelete(delDb.id)
                .then(() => {
                  localStorage.removeItem(`${STORAGE_KEYS.CACHE_TBL}${delDb.id}`)
                  if (allItems) {
                    setAllItems(allItems.filter((at) => at.id !== delDb.id))
                  }
                  toasted(t('Database removed'))
                })
                .catch((e) => {
                  printAPIError(e)
                })
                .finally(() => {
                  closeDel()
                })
            }}
            continueLabel={t('Yes, remove')}
            title={t('Remove database')}
          >
            <div className='modal-md max-w-full'>
              <div className='text-base mb-2'>{t("Are you sure you'd like to remove this database?")}</div>
              <div className='text-light'>
                {t(
                  'In 14 days, this database will be permanently deleted. To restore it before then, visit your Trash folder.'
                )}
              </div>
            </div>
          </DeleteModal>
        ))}
    </>
  )
}

/**
 * the main display list of db
 */
export default function OrgDatabaseList({ title }: WrapperProps) {
  const {
    allItems,
    setCurrentItems,
    currentItems,
    page,
    setPage,
    search,
    setSearch,
    debounceSearch,
    resetParams,
    handleList,
  } = useDbContext()

  useEffect(() => {
    const offset = (page - 1) * DEFAULT_ROW_PER_PAGE
    const filteredData = debounceSearch
      ? allItems?.filter((dt) => dt.name.toLowerCase().includes(debounceSearch.toLowerCase()))
      : allItems

    // Add sorting by last updated date (newest first)
    const sortedData = [...(filteredData || [])].sort(
      (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
    )

    const curData = sortedData?.slice(offset, offset + DEFAULT_ROW_PER_PAGE)

    setCurrentItems({
      items: curData || [],
      totalCount: sortedData?.length ?? 0,
    })
  }, [allItems, debounceSearch, page, setCurrentItems])

  const canAddNewDb = useOrgPerm(OrgPermission.DBCreateLinkDatabasesAndTables)

  return (
    <Layout
      title={title}
      search={search}
      onSearch={setSearch}
      onClearSearch={resetParams}
      actions={
        canAddNewDb ? (
          <CreateDbProvider backButtonText='Cancel'>
            <CreateDbModal onFinally={() => handleList()} />
          </CreateDbProvider>
        ) : undefined
      }
    >
      <DbTable />
      <WrappedPagination
        id='db-pg'
        perPage={DEFAULT_ROW_PER_PAGE}
        totalCount={currentItems.totalCount}
        page={page}
        onChange={(_f, _t, newPage) => {
          if (page !== newPage) {
            setPage(newPage)
          }
        }}
      />
    </Layout>
  )
}
